import { useState, useEffect } from 'react';
import type { AxiosInstance } from 'axios';
import type { BackboneService } from '../backbone.types';
import {
    fetchBackboneList,
    mapBbaasItemToBackboneService,
} from '../requests/request';

/**
 * Load backbone (BBAAS) detail by id from the tenant list:
 * GET /kontroledge/bbaas?tenantId=&limit=&offset=
 */
export function useBackboneDetails(
    backboneId: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    const [raw, setRaw] = useState<BackboneService | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!backboneId) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }
        if (tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await fetchBackboneList(api, tenantId, {
                    limit: 100,
                    offset: 0,
                });
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !Array.isArray(body.data)) {
                    throw new Error(body?.message ?? 'Failed to load backbone');
                }
                const item = body.data.find((d) => String(d.id) === String(backboneId));
                if (!item) {
                    setRaw(null);
                    setError('Backbone not found');
                    return;
                }
                setRaw(mapBbaasItemToBackboneService(item));
                setError(null);
            } catch (e) {
                console.error('Error in useBackboneDetails:', e);
                if (!cancelled) {
                    setRaw(null);
                    setError(e instanceof Error ? e.message : 'Failed to load backbone');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [backboneId, tenantId, api, refresh]);

    return { raw, loading, error };
}
